import { useEffect, useMemo, useState } from "react";
import api, { formatTL, formatDateTime } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { FileText, FileDown, Eye, Trash2, ArrowRightLeft, Search, Pencil } from "lucide-react";
import { ConfirmDialog } from "../../components/common";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { downloadQuotePDF } from "../../lib/pdfGenerator";
import QuoteCustomerView from "../../components/QuoteCustomerView";

const STATUS_BADGE = {
  beklemede: { label: "Beklemede", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  kabul: { label: "Kabul Edildi", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  red: { label: "Reddedildi", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  iptal: { label: "İptal", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function AdminQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewQuote, setViewQuote] = useState(null);
  const [delQuote, setDelQuote] = useState(null);
  const [editQuote, setEditQuote] = useState(null);
  const [convertQuote, setConvertQuote] = useState(null);
  const [convertForm, setConvertForm] = useState({ customer_id: "", assigned_technician_id: "", priority: "normal" });

  const load = async () => {
    const [q, c, t, s] = await Promise.all([
      api.get("/quotes"),
      api.get("/customers"),
      api.get("/technicians"),
      api.get("/settings"),
    ]);
    setQuotes(q.data || []);
    setCustomers(c.data || []);
    setTechs(t.data || []);
    setSettings(s.data || {});
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return quotes.filter(q =>
      (statusFilter === "all" || q.status === statusFilter) &&
      (s === "" ||
        (q.customer_name || "").toLowerCase().includes(s) ||
        (q.quote_number || "").toLowerCase().includes(s) ||
        (q.technician_name || "").toLowerCase().includes(s))
    );
  }, [quotes, search, statusFilter]);

  const totals = useMemo(() => {
    const sum = filtered.reduce((s, q) => s + (Number(q.target_total) || 0), 0);
    return { count: filtered.length, sum };
  }, [filtered]);

  const downloadPDF = async (q) => {
    try { await downloadQuotePDF(q, { fallbackFooter: settings.quote_footer_note }); }
    catch { toast.error("PDF oluşturulamadı"); }
  };

  const deleteQuote = async () => {
    await api.delete(`/quotes/${delQuote.id}`);
    toast.success("Silindi"); setDelQuote(null); load();
  };
  const saveEdit = async () => {
    const e = editQuote;
    try {
      await api.put(`/quotes/${e.id}`, {
        customer_name: e.customer_name, customer_phone: e.customer_phone, device_model: e.device_model,
        fault_description: e.fault_description, target_total: Number(e.target_total) || 0, status: e.status,
      });
      toast.success("Teklif güncellendi"); setEditQuote(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openConvert = (q) => {
    setConvertQuote(q);
    setConvertForm({
      customer_id: q.customer_id || "",
      assigned_technician_id: q.technician_id || "",
      priority: "normal",
    });
  };

  const confirmConvert = async () => {
    if (!convertForm.customer_id) { toast.error("Müşteri seçin"); return; }
    try {
      const res = await api.post(`/quotes/${convertQuote.id}/convert`, convertForm);
      toast.success("Servis kaydı oluşturuldu");
      setConvertQuote(null);
      navigate(`/admin/jobs/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dönüştürülemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-quotes-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Fiyat Teklifleri</h1>
        <p className="text-sm text-slate-500 mt-1">Teknisyenlerin müşteriye sunduğu tüm fiyat teklifleri</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border border-slate-200 shadow-none">
          <div className="text-xs uppercase tracking-wider text-slate-500">Toplam Teklif</div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-2">{totals.count}</div>
        </Card>
        <Card className="p-4 border border-slate-200 shadow-none">
          <div className="text-xs uppercase tracking-wider text-slate-500">Toplam Sunulan Tutar</div>
          <div className="font-display text-2xl font-bold text-blue-700 mt-2">{formatTL(totals.sum)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 shadow-none">
          <div className="text-xs uppercase tracking-wider text-slate-500">Kabul Edilen</div>
          <div className="font-display text-2xl font-bold text-emerald-700 mt-2">
            {quotes.filter(q => q.status === "kabul").length}
          </div>
        </Card>
      </div>

      <Card className="p-3 border border-slate-200 shadow-none flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Müşteri, teklif no veya teknisyen ara..." value={search} onChange={e => setSearch(e.target.value)} data-testid="quotes-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="beklemede">Beklemede</SelectItem>
            <SelectItem value="kabul">Kabul Edilen</SelectItem>
            <SelectItem value="red">Reddedilen</SelectItem>
            <SelectItem value="iptal">İptal</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Teklif bulunamadı</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Teklif No</th>
                <th className="px-4 py-3 font-semibold">Müşteri</th>
                <th className="px-4 py-3 font-semibold">Teknisyen</th>
                <th className="px-4 py-3 font-semibold">Arıza</th>
                <th className="px-4 py-3 font-semibold text-right">Toplam</th>
                <th className="px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3 font-semibold">Tarih</th>
                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(q => (
                <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">{q.quote_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{q.customer_name}</div>
                    <div className="text-xs text-slate-500">{q.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{q.technician_name || "-"}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{q.fault_description}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">{formatTL(q.target_total)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[q.status]?.cls}`}>
                      {STATUS_BADGE[q.status]?.label}
                    </span>
                    {q.converted_job_id && (
                      <button onClick={() => navigate(`/admin/jobs/${q.converted_job_id}`)} className="block text-[10px] text-blue-600 hover:underline mt-1">
                        Servisi Aç →
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{formatDateTime(q.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setViewQuote(q)} title="Görüntüle" data-testid={`v-${q.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditQuote({ ...q })} title="Düzenle" data-testid={`e-${q.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPDF(q)} title="PDF" data-testid={`p-${q.id}`}>
                        <FileDown className="h-3.5 w-3.5" />
                      </Button>
                      {!q.converted_job_id && (
                        <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => openConvert(q)} title="Servis Kaydına Dönüştür" data-testid={`c-${q.id}`}>
                          <ArrowRightLeft className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDelQuote(q)} title="Sil" data-testid={`d-${q.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Müşteri Görünüm */}
      {viewQuote && (
        <QuoteCustomerView
          quote={viewQuote}
          settings={settings}
          onClose={() => setViewQuote(null)}
          onDownload={() => downloadPDF(viewQuote)}
          onShare={() => {}}
        />
      )}

      <Dialog open={!!editQuote} onOpenChange={(o) => !o && setEditQuote(null)}>
        <DialogContent data-testid="quote-edit-dialog">
          <DialogHeader><DialogTitle>Teklifi Düzenle — {editQuote?.quote_number}</DialogTitle></DialogHeader>
          {editQuote && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Müşteri</Label><Input value={editQuote.customer_name || ""} onChange={(e) => setEditQuote({ ...editQuote, customer_name: e.target.value })} /></div>
                <div><Label>Telefon</Label><Input value={editQuote.customer_phone || ""} onChange={(e) => setEditQuote({ ...editQuote, customer_phone: e.target.value })} /></div>
              </div>
              <div><Label>Cihaz modeli</Label><Input value={editQuote.device_model || ""} onChange={(e) => setEditQuote({ ...editQuote, device_model: e.target.value })} /></div>
              <div><Label>Arıza</Label><Input value={editQuote.fault_description || ""} onChange={(e) => setEditQuote({ ...editQuote, fault_description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Toplam tutar (₺)</Label><Input type="number" value={editQuote.target_total ?? 0} onChange={(e) => setEditQuote({ ...editQuote, target_total: e.target.value })} /></div>
                <div><Label>Durum</Label>
                  <Select value={editQuote.status} onValueChange={(v) => setEditQuote({ ...editQuote, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beklemede">Beklemede</SelectItem><SelectItem value="kabul">Kabul</SelectItem>
                      <SelectItem value="red">Red</SelectItem><SelectItem value="iptal">İptal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditQuote(null)}>Vazgeç</Button>
            <Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="quote-edit-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!delQuote} onOpenChange={(v) => !v && setDelQuote(null)} title="Teklif silinsin mi?" description={delQuote?.quote_number} onConfirm={deleteQuote} />

      {/* Convert Dialog */}
      <Dialog open={!!convertQuote} onOpenChange={(o) => !o && setConvertQuote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Teklifi Servis Kaydına Dönüştür</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 rounded p-3 text-sm">
              <div><b>{convertQuote?.customer_name}</b> — {formatTL(convertQuote?.target_total)}</div>
              <div className="text-xs text-slate-500 mt-1">{convertQuote?.fault_description}</div>
            </div>
            <div>
              <Label>Mevcut Müşteri Seç *</Label>
              <Select value={convertForm.customer_id} onValueChange={(v) => setConvertForm({ ...convertForm, customer_id: v })}>
                <SelectTrigger data-testid="convert-customer"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} • {c.phone}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1">Yeni müşteriyse önce "Müşteriler" sayfasından ekleyin.</div>
            </div>
            <div>
              <Label>Teknisyen</Label>
              <Select value={convertForm.assigned_technician_id} onValueChange={(v) => setConvertForm({ ...convertForm, assigned_technician_id: v })}>
                <SelectTrigger><SelectValue placeholder="Teknisyen seçin" /></SelectTrigger>
                <SelectContent>
                  {techs.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Öncelik</Label>
              <Select value={convertForm.priority} onValueChange={(v) => setConvertForm({ ...convertForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="acil">ACİL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertQuote(null)}>Vazgeç</Button>
            <Button onClick={confirmConvert} data-testid="confirm-convert">Servis Kaydı Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
